import { useNavigate, useParams } from '@/hooks/useNavigate.ts';
import { useApplyJobDetails, useApplyJobSubmission } from '@/api/services.ts';
import { Input } from '@/components/Input.tsx';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button.tsx';
import { z } from 'zod';
import {
  ControllerFieldState,
  useForm,
  type SubmitErrorHandler,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { routes } from '@/constants.ts';
import { ArrowRight } from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle.ts';
import { cn } from '@/lib/utils.ts';
import { Trans, useTranslation } from 'react-i18next';
import { useAppLanguage } from '@/hooks/useAppLanguage.ts';
import { useBrandColors } from '@/hooks/useBrandColors.ts';
import { useEffectiveLanguage } from '@/hooks/useEffectiveLanguage.ts';
import { generatePath } from 'react-router-dom';
import { isInAppBrowser } from '@/pages/Answers/InAppBrowserBlock.tsx';
import { actionColor } from '@/lib/brandColor.ts';
import { ApplyFlowShell } from './components/ApplyFlowShell';
import { StepProgress } from './components/StepProgress';
import { normalizeLanguages } from '@/lib/language';
import { containsCyrillic, interviewHandoffPath } from './applyJobUtils';

type ApplyFormValues = {
  position_id: string;
  first_name: string;
  last_name: string;
  phone_number: string;
};

// Schema / visual order of the fields — used to pick the first invalid one so a
// failed submit scrolls to (and focuses) it. RHF's own shouldFocusError can't
// reach the Position Select (no registered ref) or the masked phone input, so we
// drive scroll + focus by DOM id here. The ids are `position_id` on the
// SelectTrigger and `first_name`/`last_name`/`phone_number` on the inputs.
const FIELD_ORDER: (keyof ApplyFormValues)[] = [
  'position_id',
  'first_name',
  'last_name',
  'phone_number',
];

const focusFirstInvalidField: SubmitErrorHandler<ApplyFormValues> = (errors) => {
  const firstInvalid = FIELD_ORDER.find((name) => errors[name]);
  if (!firstInvalid) return;
  const el = document.getElementById(firstInvalid);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  // preventScroll so the focus jump doesn't fight the smooth scrollIntoView.
  el.focus({ preventScroll: true });
};

const useInterviewSubmissionForm = () => {
  const { t } = useTranslation('applyJob');
  const latinName = (requiredMessage: string) =>
    z
      .string()
      .min(1, requiredMessage)
      .refine((value) => !containsCyrillic(value), t('validation.latin_only'));
  const schema = z.object({
    position_id: z.string().min(1, t('validation.position_required')),
    first_name: latinName(t('validation.first_name_required')),
    last_name: latinName(t('validation.last_name_required')),
    phone_number: z.string().min(1, t('validation.phone_number_required')),
  });
  return useForm<ApplyFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    shouldFocusError: false, // handled manually in focusFirstInvalidField
    defaultValues: {
      position_id: '',
      first_name: '',
      last_name: '',
      phone_number: '',
    },
  });
};

const ApplyJob = () => {
  const [language, setLanguage] = useAppLanguage();

  const navigate = useNavigate();
  const { t } = useTranslation('applyJob');
  const params = useParams<{ property_id: string }>();
  const {
    data: jobDetails,
    isLoading,
    isError,
  } = useApplyJobDetails(params.property_id || '', language);
  const { availableLanguages, effectiveLanguage } = useEffectiveLanguage(
    language,
    jobDetails?.positions
  );
  const normalizedLanguages = normalizeLanguages(availableLanguages);
  const { mutateAsync: triggerSubmissionApply, isLoading: isSubmitting } =
    useApplyJobSubmission(params.property_id || '');

  const { backgroundColor, textColor } = useBrandColors(jobDetails);
  const hookForm = useInterviewSubmissionForm();

  const onSubmit = async (data: ApplyFormValues) => {
    try {
      const response = await triggerSubmissionApply(data);
      let nextPath = interviewHandoffPath(response.invitation_key);
      const inApp = isInAppBrowser();
      if (inApp) {
        nextPath = generatePath(routes.applyFormFinish, {
          property_id: params.property_id || '',
        });
      }
      navigate(nextPath);
    } catch (error) {
      const errors = JSON.parse((error as Error).message);
      for (const field in errors) {
        if (field in data) {
          hookForm.setError(field as keyof typeof data, {
            type: 'custom',
            message: errors[field][0],
          });
        }
      }
    }
  };

  usePageTitle(jobDetails?.client_name, 'apply');

  if (isLoading) {
    return (
      <div className="grid min-h-[100dvh] place-items-center bg-[#f4f4f5] text-[#9aa0a8]">
        <div className="size-8 animate-spin rounded-full border-2 border-current border-t-transparent" />
      </div>
    );
  }

  const noData =
    isError || !jobDetails || jobDetails.positions.length === 0
      ? { title: t('no_positions_title'), body: t('no_positions_body') }
      : undefined;

  const inputClasses =
    'h-12 rounded-md border border-[#e3e4e8] font-medium text-[#16171a] ' +
    // Clear the pinned brand hero (+ room for the field label) when a failed
    // submit scrolls this field into view. --hero-expanded is inherited from
    // .apply-flow-shell and is already responsive.
    'scroll-mt-[calc(var(--hero-expanded,168px)_+_2rem)]';
  const labelClasses = 'text-sm font-medium text-[#54585f]';

  return (
    <ApplyFlowShell
      bgColor={backgroundColor}
      inkColor={textColor}
      logoUrl={jobDetails?.logo_url ?? ''}
      brandName={jobDetails?.client_name}
      welcomeTitle={
        <Trans
          i18nKey="applyJob:welcome_title"
          components={{
            br: <br />,
          }}
        />
      }
      language={effectiveLanguage}
      availableLanguages={normalizedLanguages}
      onLanguageChange={setLanguage}
      onSubmit={hookForm.handleSubmit(onSubmit, focusFirstInvalidField)}
      noData={noData}
      footer={
        <Button
          variant="brand"
          className="w-full gap-2 rounded-xl max-w-md short:h-[52px]"
          size="lg"
          disabled={isSubmitting}
          type="submit"
        >
          {t('start_interview_button')}
          <ArrowRight className="size-4" aria-hidden />
        </Button>
      }
      lockHero="full"
    >
      {jobDetails && (
        <>
          <StepProgress
            step={2}
            brandColor={actionColor(backgroundColor)}
            label={t('step_progress', { step: 2, total: 3 })}
            lastStepLabel={t('interview_step')}
          />
          <Form {...hookForm}>
            <div className="mt-4 flex flex-col gap-3 short:mt-3 short:gap-1.5">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-[#8a8f98] short:hidden">
                {t('welcome_message')}
              </div>

              <FormField
                control={hookForm.control}
                name="position_id"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel className={labelClasses} errorClassName="">
                      {t('position_label')}
                    </FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={
                          isSubmitting || jobDetails.positions.length === 0
                        }
                      >
                        <SelectTrigger
                          id="position_id"
                          className={cn(
                            'w-full',
                            inputClasses,
                            getInputErrorClasses(fieldState)
                          )}
                        >
                          <SelectValue
                            placeholder={t('positions_available', {
                              count: jobDetails.positions.length,
                            })}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {jobDetails.positions.map((position) => (
                              <SelectItem
                                key={position.value}
                                value={String(position.value)}
                              >
                                {position.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage className="" />
                  </FormItem>
                )}
              />

              <FormField
                control={hookForm.control}
                name="first_name"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel
                      htmlFor="first_name"
                      className={labelClasses}
                      errorClassName=""
                    >
                      {t('first_name_label')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="first_name"
                        type="text"
                        className={cn(
                          inputClasses,
                          getInputErrorClasses(fieldState)
                        )}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="" />
                  </FormItem>
                )}
              />

              <FormField
                control={hookForm.control}
                name="last_name"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel
                      htmlFor="last_name"
                      className={labelClasses}
                      errorClassName=""
                    >
                      {t('last_name_label')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="last_name"
                        type="text"
                        className={cn(
                          inputClasses,
                          getInputErrorClasses(fieldState)
                        )}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="" />
                  </FormItem>
                )}
              />

              <FormField
                control={hookForm.control}
                name="phone_number"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel
                      htmlFor="phone_number"
                      className={labelClasses}
                      errorClassName=""
                    >
                      {t('phone_number_label')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="phone_number"
                        type="text"
                        kind="phone_number"
                        className={cn(
                          inputClasses,
                          getInputErrorClasses(fieldState)
                        )}
                        placeholder={t('phone_number_placeholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="" />
                  </FormItem>
                )}
              />
            </div>
          </Form>
        </>
      )}
    </ApplyFlowShell>
  );
};

function getInputErrorClasses(fieldState: ControllerFieldState) {
  if (fieldState.error) {
    return 'bg-red-100';
  }

  return '';
}

export default ApplyJob;
